/**
 * AccountsInFlight.tsx — Filterable team-wide pipeline.
 * Wired to live Supabase data. If the DB returns 0 accounts,
 * shows an empty state — no demo fallback data.
 *
 * Route: /reporting/accounts
 * URL params (read from hash query string):
 *   ?bucket=COMMIT|PROBABLE|UPSIDE|OMITTED
 *   ?ae=<userId>
 *   ?stuck=true   (last activity > 14d)
 *
 * Columns: Account · Industry · CP Stage · Owner AE · Days since activity
 *          Value · Bucket · Weighted · Next Step
 * Row click → /account/:id (account scorecard)
 */
import { useEffect, useMemo, useState } from "react";
import { useLocation } from "wouter";
import OperatorShell from "@/components/operator/OperatorShell";
import {
  AccountRow,
  ForecastRow,
  UserRow,
  bucketColor,
  daysSince,
  fmtUSD,
  listAccountForecasts,
  listAccounts,
  listAllUsers,
  listCpStatusFor,
  summarizeCpStatus,
  CP_LABELS,
} from "@/lib/reporting-live";
// W36-C: Removed demo fallback (buildDemoAccounts, DEMO_USERS, DEMO_NEXT_STEPS).
// All data must come from live Tenant 1 Supabase queries.
// If the query returns empty, an empty state is shown — no placeholder data.

// ---------------------------------------------------------------------------

interface RowVM {
  account: AccountRow;
  forecast: ForecastRow | null;
  ownerName: string;
  green: number;
  scored: number;
  daysSinceActivity: number;
  nextStep: { action: string; date: string } | null;
}

function readHashParams(): URLSearchParams {
  const hash = typeof window !== "undefined" ? window.location.hash : "";
  const q = hash.indexOf("?");
  return new URLSearchParams(q >= 0 ? hash.slice(q + 1) : "");
}

// Format stage key to a human-readable CP label
function fmtStage(stage: string | null | undefined): string {
  if (!stage) return "—";
  // Matches "cp3_demonstration" → "CP3 · Pain" (legacy key, canonical label)
  const match = stage.match(/^cp(\d+)_(.+)$/);
  if (match) {
    const cp = Number(match[1]);
    const label = CP_LABELS[cp] || match[2].replace(/_/g, " ");
    return `CP${cp} · ${label}`;
  }
  // Pass through anything else (WON, LOST, etc.)
  return stage.replace(/_/g, " ").toUpperCase();
}

export default function AccountsInFlight() {
  const [, navigate] = useLocation();
  const [accounts, setAccounts] = useState<AccountRow[] | null>(null);
  const [forecasts, setForecasts] = useState<ForecastRow[]>([]);
  const [users, setUsers] = useState<UserRow[]>([]);
  const [cpByAccount, setCpByAccount] =
    useState<ReturnType<typeof summarizeCpStatus> | null>(null);
  const [error, setError] = useState<string | null>(null);

  const initialParams = readHashParams();
  const [bucketFilter, setBucketFilter] = useState<string>(initialParams.get("bucket") || "");
  const [aeFilter, setAeFilter] = useState<string>(initialParams.get("ae") || "");
  const [stuckOnly, setStuckOnly] = useState<boolean>(initialParams.get("stuck") === "true");

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const [acc, fc, us] = await Promise.all([
          listAccounts(),
          listAccountForecasts(),
          listAllUsers(),
        ]);
        if (cancelled) return;

        // Show live data (empty array is a valid empty state)
        setAccounts(acc);
        setForecasts(fc);
        setUsers(us);
        const ids = acc.map((a) => a.id);
        const cps = ids.length > 0 ? await listCpStatusFor(ids) : [];
        if (cancelled) return;
        setCpByAccount(summarizeCpStatus(cps));
      } catch (err: any) {
        if (cancelled) return;
        setAccounts([]);
        setForecasts([]);
        setUsers([]);
        setCpByAccount(new Map());
        setError(`Live data unavailable. (${err?.message || "Unknown error"})`);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, []);

  const userMap = useMemo(() => new Map(users.map((u) => [u.id, u])), [users]);
  const forecastMap = useMemo(
    () => new Map(forecasts.map((f) => [f.account_id, f])),
    [forecasts],
  );

  const rows: RowVM[] = useMemo(() => {
    if (!accounts) return [];
    return accounts.map((a) => {
      const fc = forecastMap.get(a.id) || null;
      const owner = userMap.get(a.owner_user_id);
      const cp = cpByAccount?.get(a.id);
      const lastActivity = cp?.lastUpdated || a.updated_at;
      return {
        account: a,
        forecast: fc,
        ownerName: owner?.name || owner?.full_name || owner?.email || `AE ${a.owner_user_id}`,
        green: cp?.green || 0,
        scored: cp?.scored || 0,
        daysSinceActivity: daysSince(lastActivity),
        nextStep: null,
      };
    });
  }, [accounts, forecastMap, userMap, cpByAccount]);

  const aes = useMemo(() => users.filter((u) => u.role === "ae"), [users]);

  const filtered = useMemo(() => {
    return rows.filter((r) => {
      if (bucketFilter) {
        const b = (r.forecast?.forecast_bucket || r.account.bucket || "").toUpperCase();
        if (b !== bucketFilter.toUpperCase()) return false;
      }
      if (aeFilter && String(r.account.owner_user_id) !== aeFilter) return false;
      if (stuckOnly && r.daysSinceActivity <= 14) return false;
      return true;
    });
  }, [rows, bucketFilter, aeFilter, stuckOnly]);

  const totalWeighted = useMemo(
    () => filtered.reduce((s, r) => s + Number(r.forecast?.weighted_value || 0), 0),
    [filtered],
  );

  const totalValue = useMemo(
    () => filtered.reduce((s, r) => s + Number(r.forecast?.deal_value ?? r.account.est_arr ?? 0), 0),
    [filtered],
  );

  function updateHashParams(next: { bucket?: string; ae?: string; stuck?: boolean }) {
    const cur = readHashParams();
    const obj: Record<string, string> = {};
    cur.forEach((v, k) => (obj[k] = v));
    if (next.bucket !== undefined) {
      if (next.bucket) obj.bucket = next.bucket;
      else delete obj.bucket;
    }
    if (next.ae !== undefined) {
      if (next.ae) obj.ae = next.ae;
      else delete obj.ae;
    }
    if (next.stuck !== undefined) {
      if (next.stuck) obj.stuck = "true";
      else delete obj.stuck;
    }
    const qs = new URLSearchParams(obj).toString();
    const base = (window.location.hash.split("?")[0] || "#/reporting/accounts").replace(/^#/, "");
    window.location.hash = qs ? `${base}?${qs}` : base;
  }

  return (
    <OperatorShell breadcrumbs={[{ label: "Reporting", href: "/reporting" }, { label: "Accounts in Flight" }]}>
      <div style={{ maxWidth: 1400, margin: "0 auto" }}>
        {/* Back link — satisfies modal-exit rule for routed pages */}
        <a
          href="#/reporting"
          style={{ fontSize: 12, color: "#6B6B70", textDecoration: "none", fontWeight: 500 }}
        >
          ← Reporting Hub
        </a>
        <h1 style={{ fontSize: 24, fontWeight: 900, color: "#0B0B0F", margin: "10px 0 4px" }}>
          Accounts in Flight
        </h1>
        <div style={{ fontSize: 13, color: "#6B6B70", marginBottom: 20 }}>
          Every active account across the team. Filter by bucket, AE, or stuck status.
        </div>

        <div
          style={{
            background: "#0B0B0F",
            borderRadius: 12,
            padding: "16px 24px",
            marginBottom: 20,
            display: "flex",
            gap: 32,
            flexWrap: "wrap",
          }}
        >
          <Stat label="Showing" value={`${filtered.length}`} color="#FAFAF7" />
          <Stat
            label="Of total"
            value={accounts === null ? "…" : `${accounts.length}`}
            color="#8E8E94"
          />
          <Stat label="Total ARR" value={fmtUSD(totalValue)} color="#E5E5E7" />
          <Stat label="Weighted forecast" value={fmtUSD(totalWeighted)} color="#86EFAC" />
        </div>

        <div
          style={{
            display: "flex",
            gap: 12,
            flexWrap: "wrap",
            marginBottom: 16,
            alignItems: "center",
          }}
        >
          <select
            value={bucketFilter}
            onChange={(e) => {
              setBucketFilter(e.target.value);
              updateHashParams({ bucket: e.target.value });
            }}
            style={selectStyle}
          >
            <option value="">All buckets</option>
            <option value="COMMIT">COMMIT</option>
            <option value="PROBABLE">PROBABLE</option>
            <option value="UPSIDE">UPSIDE</option>
            <option value="OMITTED">OMITTED</option>
          </select>
          <select
            value={aeFilter}
            onChange={(e) => {
              setAeFilter(e.target.value);
              updateHashParams({ ae: e.target.value });
            }}
            style={selectStyle}
          >
            <option value="">All AEs</option>
            {aes.map((a) => (
              <option key={a.id} value={a.id}>
                {a.name || a.full_name || a.email}
              </option>
            ))}
          </select>
          <label
            style={{ display: "inline-flex", alignItems: "center", gap: 6, fontSize: 13, color: "#525258" }}
          >
            <input
              type="checkbox"
              checked={stuckOnly}
              onChange={(e) => {
                setStuckOnly(e.target.checked);
                updateHashParams({ stuck: e.target.checked });
              }}
            />
            Stuck only (no activity &gt; 14d)
          </label>
          {(bucketFilter || aeFilter || stuckOnly) && (
            <button
              onClick={() => {
                setBucketFilter("");
                setAeFilter("");
                setStuckOnly(false);
                updateHashParams({ bucket: "", ae: "", stuck: false });
              }}
              style={{
                padding: "6px 12px",
                border: "1px solid #C9C9CC",
                borderRadius: 6,
                background: "#FFFFFF",
                fontSize: 12,
                color: "#525258",
                cursor: "pointer",
              }}
            >
              Clear filters
            </button>
          )}
        </div>

        {error && (
          <div
            style={{
              padding: 12,
              background: "#FEF2F2",
              border: "1px solid #FCA5A5",
              borderRadius: 8,
              color: "#991B1B",
              fontSize: 13,
              marginBottom: 16,
            }}
          >
            {error}
          </div>
        )}

        <div
          style={{
            background: "#FFFFFF",
            border: "1px solid #E5E5E7",
            borderRadius: 12,
            overflow: "hidden",
          }}
        >
          <div style={{ overflowX: "auto" }}>
            <table style={{ width: "100%", borderCollapse: "collapse", fontSize: 13, minWidth: 900 }}>
              <thead>
                <tr style={{ background: "#FAFAF7" }}>
                  {[
                    "Account",
                    "CP Stage",
                    "Owner AE",
                    "ARR / Value",
                    "Bucket",
                    "Last Activity",
                    "Next Step",
                  ].map((h) => (
                    <th
                      key={h}
                      style={{
                        padding: "10px 14px",
                        textAlign: "left",
                        fontSize: 10,
                        fontWeight: 700,
                        letterSpacing: "0.12em",
                        textTransform: "uppercase",
                        color: "#8E8E94",
                        borderBottom: "1px solid #EFEFEE",
                        whiteSpace: "nowrap",
                      }}
                    >
                      {h}
                    </th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {accounts === null ? (
                  <tr>
                    <td colSpan={7} style={{ padding: 20, color: "#8E8E94", textAlign: "center" }}>
                      Loading…
                    </td>
                  </tr>
                ) : filtered.length === 0 ? (
                  <tr>
                    <td colSpan={7} style={{ padding: 20, color: "#8E8E94", textAlign: "center" }}>
                      {accounts.length === 0
                        ? "No accounts in flight yet. Add accounts to your pipeline to see them here."
                        : "No accounts match the current filters."}
                    </td>
                  </tr>
                ) : (
                  filtered.map((r) => {
                    const bucket = (r.forecast?.forecast_bucket || r.account.bucket || "").toUpperCase();
                    const stageLabel = fmtStage(r.account.stage);
                    const arrValue = r.forecast?.deal_value ?? r.account.est_arr ?? 0;
                    const isStuck = r.daysSinceActivity > 14;

                    return (
                      <tr
                        key={r.account.id}
                        onClick={() => { window.location.hash = `/account/${r.account.id}`; }}
                        style={{ cursor: "pointer", borderTop: "1px solid #FAFAF7" }}
                        onMouseEnter={(e) =>
                          ((e.currentTarget as HTMLElement).style.background = "#FAFAF7")
                        }
                        onMouseLeave={(e) =>
                          ((e.currentTarget as HTMLElement).style.background = "#FFFFFF")
                        }
                        title="Click to open account scorecard"
                      >
                        {/* Account name + industry */}
                        <td style={{ padding: "10px 14px", color: "#0B0B0F", fontWeight: 600, maxWidth: 220 }}>
                          <div style={{ display: "flex", alignItems: "center", gap: 6 }}>
                            <span>{r.account.name}</span>
                            <span
                              style={{
                                fontSize: 10,
                                color: "#8E8E94",
                                fontWeight: 400,
                                background: "#EFEFEE",
                                borderRadius: 4,
                                padding: "1px 5px",
                              }}
                            >
                              →
                            </span>
                          </div>
                          {r.account.industry && (
                            <div style={{ fontSize: 11, color: "#8E8E94", fontWeight: 400, marginTop: 2 }}>
                              {r.account.industry}
                            </div>
                          )}
                        </td>

                        {/* CP Stage */}
                        <td style={{ padding: "10px 14px", color: "#525258", whiteSpace: "nowrap" }}>
                          {stageLabel !== "—" ? (
                            <span
                              style={{
                                padding: "2px 7px",
                                borderRadius: 4,
                                background: "#FAFAF7",
                                color: "#0B0B0F",
                                fontSize: 11,
                                fontWeight: 600,
                              }}
                            >
                              {stageLabel}
                            </span>
                          ) : (
                            <span style={{ color: "#C9C9CC" }}>—</span>
                          )}
                        </td>

                        {/* Owner AE */}
                        <td style={{ padding: "10px 14px", color: "#525258", whiteSpace: "nowrap" }}>
                          {r.ownerName}
                        </td>

                        {/* ARR / Value */}
                        <td style={{ padding: "10px 14px", color: "#0B0B0F", whiteSpace: "nowrap" }}>
                          {fmtUSD(arrValue)}
                          {r.forecast?.weighted_value ? (
                            <div style={{ fontSize: 11, color: "#8E8E94", marginTop: 2 }}>
                              {fmtUSD(r.forecast.weighted_value)} weighted
                            </div>
                          ) : null}
                        </td>

                        {/* Bucket badge */}
                        <td style={{ padding: "10px 14px", whiteSpace: "nowrap" }}>
                          {bucket ? (
                            <span
                              style={{
                                padding: "2px 8px",
                                borderRadius: 4,
                                background: bucketColor(bucket) + "22",
                                color: bucketColor(bucket),
                                fontSize: 10,
                                fontWeight: 700,
                                letterSpacing: "0.08em",
                              }}
                            >
                              {bucket}
                            </span>
                          ) : (
                            <span style={{ color: "#C9C9CC" }}>—</span>
                          )}
                        </td>

                        {/* Days since last activity */}
                        <td style={{ padding: "10px 14px", whiteSpace: "nowrap" }}>
                          <span
                            style={{
                              color: isStuck ? "#DC2626" : "#525258",
                              fontWeight: isStuck ? 700 : 400,
                            }}
                          >
                            {Number.isFinite(r.daysSinceActivity)
                              ? `${r.daysSinceActivity}d ago`
                              : "—"}
                          </span>
                          {isStuck && (
                            <div style={{ fontSize: 10, color: "#DC2626", marginTop: 1 }}>STUCK</div>
                          )}
                        </td>

                        {/* Next step */}
                        <td style={{ padding: "10px 14px", maxWidth: 260 }}>
                          {r.nextStep ? (
                            <>
                              <div style={{ fontSize: 12, color: "#0B0B0F", lineHeight: 1.4 }}>
                                {r.nextStep.action}
                              </div>
                              <div style={{ fontSize: 11, color: "#8E8E94", marginTop: 2 }}>
                                {r.nextStep.date}
                              </div>
                            </>
                          ) : (
                            <span style={{ color: "#C9C9CC", fontSize: 12 }}>—</span>
                          )}
                        </td>
                      </tr>
                    );
                  })
                )}
              </tbody>
            </table>
          </div>
        </div>

        {filtered.length > 0 && (
          <div style={{ marginTop: 12, fontSize: 12, color: "#8E8E94", textAlign: "right" }}>
            Click any row to open the account scorecard.
          </div>
        )}
      </div>
    </OperatorShell>
  );
}

const selectStyle: React.CSSProperties = {
  padding: "6px 10px",
  border: "1px solid #C9C9CC",
  borderRadius: 6,
  background: "#FFFFFF",
  fontSize: 13,
  color: "#0B0B0F",
  cursor: "pointer",
};

function Stat({ label, value, color }: { label: string; value: string | number; color: string }) {
  return (
    <div>
      <div
        style={{
          fontSize: 10,
          fontWeight: 700,
          letterSpacing: "0.1em",
          textTransform: "uppercase",
          color: "#525258",
          marginBottom: 4,
        }}
      >
        {label}
      </div>
      <div style={{ fontSize: 22, fontWeight: 800, color, lineHeight: 1 }}>{value}</div>
    </div>
  );
}
